
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLeads } from "@/context/LeadContext";
// import { usePartners } from "@/context/PartnerContext"; // Not currently used, commented out
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Calendar,
  Mail,
  ExternalLink,
  User,
  Clock,
  MessageSquare,
  FileText,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import LeadStatusBadge from "@/components/ui-custom/LeadStatusBadge";
import ServiceBadge from "@/components/ui-custom/ServiceBadge";
import { LeadStageType } from "@/context/LeadContext";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { useToast } from "@/components/ui/use-toast";

const AdminLeadDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { getLeadById, updateLeadStage } = useLeads();
  // const { partners } = usePartners(); // Not currently used
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("details");

  // Get the lead data
  const lead = id ? getLeadById(id) : undefined;

  const handleBack = () => {
    navigate("/admin/leads");
  };

  const handleUpdateStage = async (stage: LeadStageType) => {
    if (id) {
      try {
        await updateLeadStage(id, stage);
        toast({
          title: "Επιτυχής ενημέρωση",
          description: "Η κατάσταση του ενδιαφερόμενου ενημερώθηκε.",
        });
      } catch (error) {
        console.error("Error updating stage:", error);
        toast({
          title: "Αποτυχία ενημέρωσης",
          description: "Υπήρξε πρόβλημα με την ενημέρωση της κατάστασης.",
          variant: "destructive",
        });
      }
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: el });
    } catch (e) {
      return "Άγνωστη ημερομηνία";
    }
  };

  if (!lead) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <User className="h-8 w-8 text-slate-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Ο ενδιαφερόμενος δεν βρέθηκε</h1>
        <p className="text-slate-500 mb-6">Δεν βρέθηκαν στοιχεία για την επαφή που αναζητάτε.</p>
        <Button onClick={handleBack} className="btn-gradient">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Επιστροφή στη λίστα
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handleBack} className="rounded-full h-10 w-10 border-slate-200">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">{lead.name}</h1>
              <LeadStatusBadge status={lead.stage} />
            </div>
            <div className="flex items-center gap-2 mt-1 text-slate-500 text-sm">
              <Calendar className="h-3.5 w-3.5" />
              <span>Δημιουργήθηκε {formatDate(lead.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`/lead/${lead.id}`, '_blank')}
            className="border-slate-200"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Προβολή Συνεργάτη
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-lg w-full max-w-md grid grid-cols-3 mb-6">
          <TabsTrigger value="details" className="rounded-md data-[state=active]:bg-white">
            <User className="w-4 h-4 mr-2" />
            Στοιχεία
          </TabsTrigger>
          <TabsTrigger value="communication" className="rounded-md data-[state=active]:bg-white">
            <MessageSquare className="w-4 h-4 mr-2" />
            Επικοινωνία
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-md data-[state=active]:bg-white">
            <Clock className="w-4 h-4 mr-2" />
            Ιστορικό
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact Info */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">Προσωπικά Στοιχεία</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 rounded-full bg-slate-100">
                    <Phone className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 block">Τηλέφωνο</span>
                    <a href={`tel:${lead.phone}`} className="text-lg font-medium text-slate-900 hover:text-blue-600">
                      {lead.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1 p-1.5 rounded-full bg-slate-100">
                    <MapPin className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 block">Διεύθυνση</span>
                    <span className="text-base text-slate-900 block">{lead.address}</span>
                    <span className="text-sm text-slate-500">ΤΚ: {lead.postalCode}</span>

                    {(lead.latitude && lead.longitude) && (
                      <Badge variant="outline" className="mt-2 bg-green-50 text-green-700 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Γεωγραφικό Στίγμα Επιβεβαιωμένο
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interest Details */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-violet-50">
                    <FileText className="h-4 w-4 text-violet-600" />
                  </div>
                  <CardTitle className="text-lg">Λεπτομέρειες Ενδιαφέροντος</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-slate-500 block mb-2">Υπηρεσία</span>
                    <ServiceBadge service={lead.service} />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-500 block mb-2">Χρονοδιάγραμμα</span>
                    <Badge
                      variant="outline"
                      className={`
                        ${lead.timeframe === "άμεσα" ? "bg-red-50 text-red-700 border-red-200" :
                          lead.timeframe === "εντός3μηνών" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-slate-50 text-slate-700 border-slate-200"}
                      `}
                    >
                      {lead.timeframe === "άμεσα" ? "Άμεσα" :
                        lead.timeframe === "εντός3μηνών" ? "Εντός 3 μηνών" :
                          lead.timeframe === "κάνωΈρευνα" ? "Έρευνα Αγοράς" : lead.timeframe}
                    </Badge>
                  </div>
                </div>

                {lead.comments && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-2">Σχόλια</span>
                    <p className="text-sm text-slate-700 italic">"{lead.comments}"</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assignment & Status */}
            <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-emerald-50">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  </div>
                  <CardTitle className="text-lg">Διαχείριση & Ανάθεση</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid gap-8 md:grid-cols-2">
                  <div className="space-y-3">
                    <span className="text-sm font-medium text-slate-500 block">Ανάθεση σε Συνεργάτη</span>
                    {lead.partnerId ? (
                      <div className="flex items-center gap-3 p-3 border border-blue-100 bg-blue-50/50 rounded-lg">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                          {lead.partnerName?.charAt(0) || "P"}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{lead.partnerName || "Άγνωστος Συνεργάτης"}</p>
                          <p className="text-xs text-slate-500">Ανατέθηκε: {formatDate(lead.updatedAt)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 border border-amber-200 bg-amber-50 rounded-lg text-amber-800">
                        <AlertCircle className="h-5 w-5" />
                        <span className="font-medium">Δεν έχει γίνει ανάθεση</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <span className="text-sm font-medium text-slate-500 block">Ενημέρωση Κατάστασης</span>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStage("σεΕκκρεμότητα")}
                        disabled={lead.stage === "σεΕκκρεμότητα"}
                        className={lead.stage === "σεΕκκρεμότητα" ? "bg-amber-100 text-amber-800 border-amber-200" : ""}
                      >
                        Σε Εκκρεμότητα
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStage("ήθελεΠληροφορίες")}
                        disabled={lead.stage === "ήθελεΠληροφορίες"}
                        className={lead.stage === "ήθελεΠληροφορίες" ? "bg-blue-100 text-blue-800 border-blue-200" : ""}
                      >
                        Ήθελε Πληροφορίες
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStage("έκλεισεΗΔουλειά")}
                        disabled={lead.stage === "έκλεισεΗΔουλειά"}
                        className={lead.stage === "έκλεισεΗΔουλειά"
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : "hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200"}
                      >
                        Έκλεισε η Δουλειά
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="communication">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Επικοινωνία</CardTitle>
              <CardDescription>Διαχείριση μηνυμάτων και ιστορικό επικοινωνίας</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 rounded-xl p-8 text-center border border-slate-200 border-dashed">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <Mail className="h-6 w-6 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">Κέντρο Μηνυμάτων</h3>
                <p className="text-slate-500 max-w-sm mx-auto mb-6">
                  Για να στείλετε SMS ή να δείτε το πλήρες ιστορικό επικοινωνίας, παρακαλώ μεταβείτε στη σελίδα Μηνύματα.
                </p>
                <Button onClick={() => navigate('/admin/messages')} className="btn-gradient">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Μετάβαση στα Μηνύματα
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Χρονολόγιο</CardTitle>
              <CardDescription>Ιστορικό ενεργειών και αλλαγών</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative border-l border-slate-200 ml-3 space-y-8 py-2">

                {/* Created */}
                <div className="relative pl-8">
                  <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-slate-300 ring-4 ring-white" />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <p className="font-medium text-slate-900">Δημιουργία Lead</p>
                    <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded">
                      {formatDate(lead.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Εισαγωγή στο σύστημα</p>
                </div>

                {/* Viewed */}
                {lead.viewed && lead.viewedAt && (
                  <div className="relative pl-8">
                    <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-blue-400 ring-4 ring-white" />
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <p className="font-medium text-slate-900">Προβολή από Συνεργάτη</p>
                      <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded">
                        {formatDate(lead.viewedAt)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Ο συνεργάτης άνοιξε την καρτέλα του lead
                    </p>
                  </div>
                )}

                {/* Status Update */}
                {lead.smsStatus && (
                  <div className="relative pl-8">
                    <span className={`absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-white ${lead.smsStatus === 'sent' ? 'bg-emerald-500' : 'bg-red-500'
                      }`} />
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <p className="font-medium text-slate-900">
                        {lead.smsStatus === 'sent' ? 'Επιτυχής Αποστολή SMS' : 'Αποτυχία Αποστολής SMS'}
                      </p>
                      <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded">
                        {formatDate(lead.updatedAt)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {lead.smsStatus === 'sent'
                        ? 'Το μήνυμα εστάλη επιτυχώς στον συνεργάτη'
                        : 'Παρουσιάστηκε σφάλμα κατά την αποστολή'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLeadDetails;
